import { format } from 'date-fns';

interface AdminLog {
  id: string;
  user_id: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  details: unknown;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  user_email?: string;
}

/**
 * Exports logs to CSV format
 */
export function exportToCSV(logs: AdminLog[]): string {
  const headers = [
    'Timestamp',
    'Action',
    'Entity Type',
    'Entity ID',
    'User Email',
    'User ID',
    'IP Address',
    'Details',
  ];

  const rows = logs.map(log => [
    format(new Date(log.created_at), 'yyyy-MM-dd HH:mm:ss'),
    log.action,
    log.entity_type,
    log.entity_id || '',
    log.user_email || '',
    log.user_id || '',
    log.ip_address || '',
    log.details ? JSON.stringify(log.details) : '',
  ]);

  // Escape CSV values
  const escapeCSV = (value: string): string => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(escapeCSV).join(',')),
  ].join('\n');

  return csvContent;
}

/**
 * Exports logs to JSON format
 */
export function exportToJSON(logs: AdminLog[]): string {
  const exportData = logs.map(log => ({
    timestamp: log.created_at,
    action: log.action,
    entity_type: log.entity_type,
    entity_id: log.entity_id,
    user_email: log.user_email,
    user_id: log.user_id,
    ip_address: log.ip_address,
    user_agent: log.user_agent,
    details: log.details,
  }));

  return JSON.stringify(exportData, null, 2);
}

/**
 * Downloads a file with the given content
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export logs with the specified format
 */
export function exportLogs(logs: AdminLog[], exportFormat: 'csv' | 'json'): void {
  const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
  
  if (exportFormat === 'csv') {
    const csvContent = exportToCSV(logs);
    downloadFile(csvContent, `admin-logs_${timestamp}.csv`, 'text/csv');
  } else {
    const jsonContent = exportToJSON(logs);
    downloadFile(jsonContent, `admin-logs_${timestamp}.json`, 'application/json');
  }
}
